import { useState, useCallback } from "react";
import { createCompetitorPricingReport } from "../../utils/api/reportApi";
import {
  BenchmarkViewer,
  TargetAudienceViewer,
  GenericViewer,
  MarketViewer,
  MarketViewerV2,
  CompetitorViewer,
  MonthlyIdeationViewer,
  MetaReportViewer,
  GoogleSearchConsoleViewer,
  GoogleMerchantCenterViewer,
  RevenueRealityReportViewer,
  YearlyContentCalendarViewer,
  SeoViewer,
  AdsAuditViewer,
  InfluencerViewer,
  HistoricalOrdersViewer,
  DailyOverviewViewer,
  HomepageAssessmentViewer,
  SocialMediaReportViewer,
  SalesOrdersRollingViewer,
} from "../../components/reportViewers";
import ConversionAndPdpViewer from "../../components/reportViewers/ConversionAndPdpViewer";
import skeletonStyles from "../ReportDetailPage/ReportDetailPage.module.css";

const ReportSkeleton = () => (
  <div className={skeletonStyles.reportSkeleton}>
    {/* Hero block */}
    <div className={skeletonStyles.reportSkeletonCard}>
      <div className={skeletonStyles.reportSkeletonCardHeader}>
        <div className={skeletonStyles.reportSkeletonLine} style={{ height: 12, width: 80 }} />
        <div className={skeletonStyles.reportSkeletonLine} style={{ height: 12, width: 48, marginLeft: 'auto' }} />
      </div>
      <div className={skeletonStyles.reportSkeletonRows}>
        <div className={skeletonStyles.reportSkeletonLine} style={{ height: 28, width: '70%' }} />
        <div className={skeletonStyles.reportSkeletonLine} style={{ height: 14, width: '100%' }} />
        <div className={skeletonStyles.reportSkeletonLine} style={{ height: 14, width: '90%' }} />
        <div className={skeletonStyles.reportSkeletonLine} style={{ height: 14, width: '80%' }} />
      </div>
    </div>
    {/* Metric row */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {[1, 2, 3].map((i) => (
        <div key={i} className={skeletonStyles.reportSkeletonCard}>
          <div className={skeletonStyles.reportSkeletonLine} style={{ height: 11, width: '60%' }} />
          <div className={skeletonStyles.reportSkeletonLine} style={{ height: 24, width: '80%' }} />
        </div>
      ))}
    </div>
    {/* Content block */}
    <div className={skeletonStyles.reportSkeletonCard}>
      <div className={skeletonStyles.reportSkeletonRows}>
        {[100, 88, 95, 72, 83].map((w) => (
          <div key={w} className={skeletonStyles.reportSkeletonLine} style={{ height: 13, width: `${w}%` }} />
        ))}
      </div>
    </div>
    {/* Second content block */}
    <div className={skeletonStyles.reportSkeletonCard}>
      <div className={skeletonStyles.reportSkeletonLine} style={{ height: 12, width: 100 }} />
      <div className={skeletonStyles.reportSkeletonRows}>
        {[92, 75, 60, 85].map((w) => (
          <div key={w} className={skeletonStyles.reportSkeletonLine} style={{ height: 13, width: `${w}%` }} />
        ))}
      </div>
    </div>
  </div>
);

const ReportViewerContent = ({
  report,
  focusedView,
  activeTab,
  activeRevenueTab,
  isMonthlySubscription,
  reportLoading = false,
  hasFullMetaPayload = false,
  onOpenChannel = null,
  authToken = null,
  onWindowChange = null,
}) => {
  const data = report?.data || {};
  const [pricingReport, setPricingReport] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  const handleGeneratePricing = useCallback(async () => {
    if (pricingLoading) return;
    const userId = String(report?.user_id || "").trim();
    const shopId = String(report?.shop_id || "").trim() || null;
    if (!userId) return;
    setPricingLoading(true);
    try {
      const result = await createCompetitorPricingReport({ userId, shopId, authToken });
      if (result?.report) setPricingReport(result.report);
    } catch (err) {
      console.error("[ReportViewerContent] competitor pricing error:", err?.message);
    } finally {
      setPricingLoading(false);
    }
  }, [report, authToken, pricingLoading]);
  const col = data?.collection_name;
  console.log("[report-viewer] resolve", {
    reportId: report?._id || report?.id || null,
    collectionName: col || null,
    focusedView: focusedView || null,
    activeTab: activeTab || null,
    activeRevenueTab: activeRevenueTab || null,
    reportLoading,
  });

  if (!report && reportLoading) {
    return <ReportSkeleton />;
  }

  switch (col) {
    case "shop_benchmark_report":
      return <BenchmarkViewer data={data} view={focusedView} />;
    case "shop_target_audience":
      return <TargetAudienceViewer data={data} />;
    case "shop_market_analysis":
      return <MarketViewer data={data} />;
    case "shop_market_analysis_v2":
      return <MarketViewerV2 data={data} authToken={authToken} />;
    case "shop_competitor_analysis":
      return (
        <CompetitorViewer
          data={data}
          pricingReport={pricingReport}
          onGeneratePricing={handleGeneratePricing}
          pricingLoading={pricingLoading}
        />
      );
    case "monthly_ideation":
      return <MonthlyIdeationViewer data={data} />;
    case "shop_meta":
      return (
        <MetaReportViewer
          report={report}
          reportLoading={reportLoading}
          hasFullMetaPayload={hasFullMetaPayload}
        />
      );
    case "yearly_content_calendar":
      return <YearlyContentCalendarViewer data={data} />;
    case "shop_seo_reports":
      return <SeoViewer data={data} />;
    case "ads_audit":
      return <AdsAuditViewer data={data} />;
    case "influencer_discovery":
      return <InfluencerViewer data={data} />;
    case "shop_daily_overview":
      console.log("[report-viewer] render DailyOverviewViewer", {
        reportId: report?._id || report?.id || null,
      });
      return <DailyOverviewViewer data={data} report={report} />;
    case "homepage_assessment":
      return <HomepageAssessmentViewer data={data} report={report} onWindowChange={onWindowChange} />;
    case "social_media_report":
      return <SocialMediaReportViewer data={data} report={report} onWindowChange={onWindowChange} />;
    case "conversion_and_pdp_report":
      console.log("[report-viewer] render ConversionAndPdpViewer", {
        reportId: report?._id || report?.id || null,
        generationKey: data?.generation_key || null,
        reportingPeriod: data?.reporting_period || null,
      });
      return (
        <ConversionAndPdpViewer
          data={data}
          report={report}
          onOpenChannel={onOpenChannel}
          onWindowChange={onWindowChange}
        />
      );
    case "sales_orders_rolling_report":
      return <SalesOrdersRollingViewer data={data} report={report} />;
    case "historical_orders_data":
      return <HistoricalOrdersViewer data={data} />;
    case "google_search_console":
      return <GoogleSearchConsoleViewer data={data} activeTab={activeTab} />;
    case "google_merchant_center":
      return <GoogleMerchantCenterViewer data={data} />;
    case "revenue_reality_full_report":
      return (
        <RevenueRealityReportViewer
          data={data}
          activeTab={activeRevenueTab}
          isMonthlySubscription={isMonthlySubscription}
        />
      );
    default:
      console.log("[report-viewer] render GenericViewer", {
        reportId: report?._id || report?.id || null,
        collectionName: col || null,
      });
      return <GenericViewer data={data} />;
  }
};

export default ReportViewerContent;
